import {
  Box,
  Button,
  ButtonVariants,
  CustomerUserConfigLayoutType,
  Expiration,
  Flex,
  FormControlSizes,
  IconButton,
  IconName,
  Input,
  MarketDataCardHeader as MarketDataCardHeaderComponent,
  MixpanelEvent,
  MixpanelEventProperty,
  SymbolDisplay,
  SymbolSelector,
  SymbolSelectorAppearance,
  VolumeLadderMarketView,
  WL_ACTION,
  isCFD,
  primeOrderForm,
  primeSwapForm,
  useHistoricalPrices,
  useIntl,
  useMarketDataSnapshot,
  useMixpanel,
  useWLLayoutType,
  useWLOrderFormDispatch,
  useWLOrgConfigContext,
  useWLRoleAuth,
  type VolumeLadderMarketViewProps,
} from '@talos/kyoko';
import { noop } from 'lodash-es';
import { useCallback, useState } from 'react';

import { DragHandle, FloatingDescription } from './styles';

import Big from 'big.js';
import { defineMessages } from 'react-intl';
import type { MarketDataCardHeaderProps } from './types';

const messages = defineMessages({
  oneMinAbbr: {
    defaultMessage: '1M',
    id: 'MarketDataCard.oneMinAbbr',
  },
  fiveMinAbbr: {
    defaultMessage: '5M',
    id: 'MarketDataCard.fiveMinAbbr',
  },
});

const RESOLUTIONS = ['1M', '5M'];

export const MarketDataCardHeader = ({
  config,
  draggableAttributes,
  draggableListeners,
  isDraggable,
  onDelete,
  onSaveCard,
  security,
}: MarketDataCardHeaderProps) => {
  const mixpanel = useMixpanel();
  const { isAuthorized } = useWLRoleAuth();
  const { config: customerConfig } = useWLOrgConfigContext();
  const { resolution = '1M', topOfBookSizeBucket } = config;
  const [qty, setQty] = useState(topOfBookSizeBucket ?? security?.MinPriceIncrement);
  const { formatMessage } = useIntl();

  const { marketDataSnapshots: marketViewObservable } = useMarketDataSnapshot({
    quantity: topOfBookSizeBucket,
    symbol: security!.Symbol,
    tag: `SECURITY_CARD_TOB`,
    priceIncrement: security?.MinPriceIncrement,
    includeTradeDirection: false,
  });

  const sparklineDataObservable = useHistoricalPrices({
    symbol: security!.Symbol,
    resolution,
  });

  const orderFormDispatch = useWLOrderFormDispatch();
  const isSwapLayout = useWLLayoutType().layoutType === CustomerUserConfigLayoutType.SwapLayout;

  const handleClickRow = useCallback(
    ({ price, side, size }) => {
      if (!security) {
        return;
      }

      mixpanel.track(MixpanelEvent.ClickLadderRow, { [MixpanelEventProperty.Side]: side });
      orderFormDispatch(
        primeOrderForm({
          Symbol: security.Symbol,
          Side: side,
          Price: price,
          OrderQty: size != null ? Big(size).toFixed() : qty,
        })
      );
      if (isSwapLayout && security.BaseCurrency) {
        orderFormDispatch(
          primeSwapForm({
            toCurrency: security.BaseCurrency,
            fromCurrency: security.QuoteCurrency,
            // if size is provided, use it instead of price
            fromQty: size ? undefined : price,
            toQty: size,
          })
        );
      }
    },
    [orderFormDispatch, mixpanel, qty, security, isSwapLayout]
  ) satisfies VolumeLadderMarketViewProps['onClickRow'];

  const handleChangeResolution = useCallback(
    (resolutionIndex: number) => {
      onSaveCard({
        ...config,
        resolution: RESOLUTIONS[resolutionIndex],
      });
    },
    [config, onSaveCard]
  );

  const handleChangeSymbol = useCallback(
    (symbol?: string) => {
      if (symbol) {
        onSaveCard({
          ...config,
          symbol,
        });
      }
    },
    [config, onSaveCard]
  );

  const handleTOBSizeBucketBlur = useCallback(() => {
    if (qty !== config.topOfBookSizeBucket && !isNaN(qty as any)) {
      onSaveCard({
        ...config,
        topOfBookSizeBucket: qty,
      });
    }
  }, [qty, config, onSaveCard]);
  const handleTOBSizeBucketKeyPress: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
    e => {
      if (e.key.toLowerCase() === 'enter') {
        handleTOBSizeBucketBlur();
      }
    },
    [handleTOBSizeBucketBlur]
  );
  const handleUpdateTOBSizeBucket: React.ChangeEventHandler<HTMLInputElement> = useCallback(
    e => {
      setQty(e.target.value);
    },
    [setQty]
  );

  const handleClickResolutionButton = useCallback(() => {
    handleChangeResolution((RESOLUTIONS.findIndex(res => res === resolution) + 1) % RESOLUTIONS.length);
  }, [handleChangeResolution, resolution]);

  const disabledSymbolSelector = !isAuthorized(WL_ACTION.USE_SYMBOL_SELECTOR);

  const topCenterItems = (
    <Flex justifyContent="center">
      {disabledSymbolSelector ? (
        <SymbolDisplay symbol={security!.Symbol} />
      ) : (
        <SymbolSelector
          appearance={SymbolSelectorAppearance.Ghost}
          symbol={security!.Symbol}
          onSymbolChanged={handleChangeSymbol}
          dropdownWidth="200px"
          disabled={disabledSymbolSelector}
          portalize={true}
          showSelectedSymbolDescription={false}
          securityRendererMaxWidth="25ch"
        />
      )}
      {isCFD(security) && !customerConfig.hideCFDLabels && (
        <FloatingDescription data-testid="market-data-card-header-floating-description">
          <Expiration>CFD</Expiration>
        </FloatingDescription>
      )}
    </Flex>
  );

  const topLeftItems = (
    <Box mt="spacing2">
      <Button
        onClick={handleClickResolutionButton}
        size={FormControlSizes.Tiny}
        data-testid="market-data-card-resolution-toggle"
        color="colors.gray.090"
      >
        {resolution === '1M' ? formatMessage(messages.oneMinAbbr) : formatMessage(messages.fiveMinAbbr)}
      </Button>
    </Box>
  );
  const topRightItems = (
    <Box>
      <IconButton
        className="delete"
        icon={IconName.Trash}
        ghost
        variant={ButtonVariants.Negative}
        onClick={() => onDelete(config)}
        size={FormControlSizes.Small}
      />
      {isDraggable && (
        <DragHandle
          icon={IconName.Reorder}
          ghost
          variant={ButtonVariants.Priority}
          onClick={noop}
          size={FormControlSizes.Small}
          {...draggableAttributes}
          {...draggableListeners}
        />
      )}
    </Box>
  );

  const bottomCenterItems = (
    <Box w="33%">
      <Input
        value={qty}
        onChange={handleUpdateTOBSizeBucket}
        onKeyUp={handleTOBSizeBucketKeyPress}
        onBlur={handleTOBSizeBucketBlur}
        controlStyle={{ textAlign: 'center' }}
        data-testid="market-data-card-top-of-book-input"
        size={FormControlSizes.Small}
      />
    </Box>
  );

  return (
    <MarketDataCardHeaderComponent
      security={security}
      slotFlexRule="0 0 55px"
      bottomCenterItems={bottomCenterItems}
      topCenterItems={topCenterItems}
      topLeftItems={topLeftItems}
      topRightItems={topRightItems}
    >
      <VolumeLadderMarketView
        resolution={resolution}
        onClickRow={handleClickRow}
        marketDataObservable={marketViewObservable}
        sparklineDataObservable={sparklineDataObservable}
        showSentiment={false}
        showSpread={customerConfig.showSpreadOnVolumeLadder}
        showReferenceLines={false}
        bpsIncrement={customerConfig.volumeLadderDecimalPrecision}
        security={security}
        usePreciseTopOfBookIncrement={customerConfig.usePreciseTopOfBookIncrement}
      />
    </MarketDataCardHeaderComponent>
  );
};
